import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { HERO_IMAGES } from "../../lib/site-data";
import { SparkleBurst } from "./Sparkle";

export const Hero = () => {
    const ref = useRef(null);

    useEffect(() => {
        const onScroll = () => {
            if (!ref.current) return;
            const y = window.scrollY;
            ref.current.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-36 pb-24 sm:pt-44 sm:pb-32 overflow-hidden bg-brand-ink text-brand-cream"
        >
            {/* Coral & gold blur ornaments — same vibe as Contact section */}
            <div className="absolute -top-44 -left-32 w-[34rem] h-[34rem] rounded-full bg-brand-coral/45 blur-3xl pointer-events-none" />
            <div className="absolute top-1/4 -right-44 w-[36rem] h-[36rem] rounded-full bg-brand-gold/30 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-44 left-1/4 w-[32rem] h-[32rem] rounded-full bg-brand-coralLight/25 blur-3xl pointer-events-none" />

            {/* Parallax glitter texture overlay — subtle */}
            <div ref={ref} className="absolute inset-0 -z-0 pointer-events-none">
                <img
                    src={HERO_IMAGES.glitter}
                    alt=""
                    className="w-full h-full object-cover opacity-[0.10] mix-blend-screen"
                    aria-hidden
                />
            </div>

            {/* Subtle holographic gradient sweep */}
            <div className="absolute inset-0 -z-0 pointer-events-none holo-bg opacity-[0.08]" />

            {/* gentle floating accent (keeps motion lively) */}
            <motion.div
                className="absolute top-44 right-[10%] w-2 h-2 rounded-full bg-brand-gold"
                animate={{ y: [0, -14, 0], opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute bottom-40 left-[14%] w-1.5 h-1.5 rounded-full bg-brand-coralLight"
                animate={{ y: [0, 12, 0], opacity: [0.3, 0.9, 0.3] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            />

            <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.1 }}
                    className="font-display text-5xl sm:text-7xl lg:text-[8rem] leading-[0.95] tracking-tight max-w-5xl"
                >
                    For moments
                    <br />
                    worth{" "}
                    <span className="italic shimmer-text-warm">celebrating.</span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25 }}
                    className="mt-7 max-w-xl text-lg sm:text-xl text-white/75 font-light leading-relaxed"
                    data-testid="hero-subtitle"
                >
                    Glow Momentz turns your moments into memories —
                    henna, face painting, and glitter tattoos that delight kids and grown-ups alike.
                    Private non-bridal henna sessions also available by appointment.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                >
                    <SparkleBurst count={18}>
                        <a
                            href="#inquiry"
                            data-testid="hero-cta-book"
                            className="group inline-flex items-center gap-2 rounded-full bg-brand-coral hover:bg-brand-coralLight text-white px-8 py-4 text-base shadow-[0_18px_50px_-12px_rgba(224,122,95,0.7)] transition-all duration-300 hover:scale-[1.02]"
                        >
                            Book your moment
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                    </SparkleBurst>
                    <a
                        href="#gallery"
                        data-testid="hero-cta-gallery"
                        className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 text-white/90 px-8 py-4 text-base transition-all hover:bg-white/5"
                    >
                        See the magic
                    </a>
                    <span className="hidden sm:inline-flex items-center gap-2 text-xs text-white/55 ml-2">
                        <MapPin className="h-3.5 w-3.5" />
                        Pittsburg, CA · Bay Area & beyond
                    </span>
                </motion.div>

                {/* mini stats */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.6 }}
                    className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 max-w-3xl"
                    data-testid="hero-stats"
                >
                    {[
                        { n: "100+", l: "Events lit up" },
                        { n: "150+", l: "Smiles painted" },
                        { n: "Bay Area", l: "Based in Pittsburg" },
                        { n: "5.0★", l: "Avg. rating" },
                    ].map((s) => (
                        <div key={s.l}>
                            <div className="font-display text-3xl sm:text-4xl text-brand-cream">{s.n}</div>
                            <div className="text-xs tracking-widest uppercase text-white/50 mt-1">{s.l}</div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
